<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import { storeToRefs } from "pinia";
import { useNotificationsStore } from "~/stores/UserNotifications";

const authStore = useAuthStore();
const indexStore = useIndexStore();
const notificationsStore = useNotificationsStore();

const { signedIn } = storeToRefs(authStore);

const parentDiv = ref<HTMLElement | null>(null);
const childDiv = ref<HTMLElement | null>(null);

const showUINotifications = ref(false);
const showUIConfig = ref(false);
const showUsers = ref(false);

const colorMode = useColorMode();
const logoURL = ref("/imgs/light-big-logo.png");

let notificationsInterval: ReturnType<typeof setInterval> | null = null;

const show = (
  uiConfig: boolean,
  uiUsers: boolean,
  uiNotifications: boolean,
) => {
  showUIConfig.value = uiConfig;
  showUsers.value = uiUsers;
  showUINotifications.value = uiNotifications;
};

function toggleSidebar() {
  indexStore.toggleSidebar();
}

const newNotification = computed(
  () => notificationsStore.notificationCount,
);

const fetchNotifications = () => {
  notificationsStore.fetchNotifications();
};

const playNotificationSound = () => {
  const audio = new Audio("/sounds/notification.mp3");
  audio.play();
};

watch(newNotification, (newVal, oldVal = 0) => {
  if (newVal > oldVal) {
    playNotificationSound();
  }
});

watch(signedIn, (isSignedIn) => {
  if (isSignedIn) {
    fetchNotifications();
    notificationsInterval = setInterval(fetchNotifications, 360000);
  } else if (notificationsInterval) {
    clearInterval(notificationsInterval);
    notificationsInterval = null;
  }
});

watch(
  () => colorMode.preference,
  (mode) => {
    logoURL.value =
      mode === "light"
        ? "/imgs/light-big-logo.png"
        : "/imgs/dark-big-logo.png";
  },
);

onMounted(() => {
  if (signedIn.value) {
    fetchNotifications();
    notificationsInterval = setInterval(fetchNotifications, 360000);
  }

  logoURL.value =
    colorMode.preference === "light"
      ? "/imgs/light-big-logo.png"
      : "/imgs/dark-big-logo.png";
});

onUnmounted(() => {
  if (notificationsInterval) {
    clearInterval(notificationsInterval);
  }
});
</script>

<template>
  <header class="navbar navbar-expand-lg">
    <button type="button" class="sidebar-toggler" @click="toggleSidebar()">
      <MGIcon icon="menu" />
    </button>

    <div class="search-bar-container">
      <!-- <search-bar-component class="mx-auto searchBar" /> -->

      <div class="mx-auto mobile-logo">
        <img :src="logoURL" width="138" height="29" :alt="$t('brands.community')" />
      </div>
    </div>

    <ul class="user-bar">
      <!-- 
	
				<li v-if="signedIn === true" class="menu-item">
					<MGIcon icon="message" />
				</li> -->

      <li class="menu-item" v-if="signedIn === true">
        <a href="#" :class="{ active: showUINotifications }"
          @click.prevent.stop="show(false, false, !showUINotifications)">
          <div v-if="newNotification !== 0" class="unread-circle mx-auto">
            {{ newNotification }}
          </div>
          <MGIcon icon="notification" />
        </a>
      </li>

      <li class="menu-item">
        <a href="#" :class="[{ active: showUIConfig }]" @click.prevent="show(!showUIConfig, false)">
          <MGIcon icon="gear" />
        </a>
      </li>

      <li ref="parentDiv" class="menu-item bordered-menu" :style="showUsers
        ? 'background: var(--header-bordered-menu-active-bg); color: black;'
        : ''
        ">
        <a href="#" :style="showUsers ? 'color: black;' : ''" @click.prevent="show(false, !showUsers)">
          <MgHeaderAvatarToggle />
        </a>
      </li>
      <transition name="fade">
        <HeaderNotificationsModal v-if="showUINotifications" @toggle-visible="show(false, false, false)" />
      </transition>
      <transition name="fade">
        <HeaderUIConfig v-if="showUIConfig" class="header_ui_config" @toggle-visible="showUIConfig = !showUIConfig" />
      </transition>
      <transition name="fade">
        <MgHeaderUIUser v-if="showUsers" ref="childDiv" class="header_ui_users"
          @toggle-visible="showUsers = !showUsers" />
      </transition>
    </ul>
  </header>
</template>

<style>
  .unread-circle {
    width: 15px;
    height: 15px;
    background-color: #ff5252;
    border-radius: 100%;
    position: absolute;
    color: white;
    font-size: 12px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding-right: 1px;
  }

</style>
<style lang="scss" scoped>
.search-bar-container {
  display: flex;
  flex: 1;
}

.mobile-logo {
  display: none;
}

.header_ui_config {
  right: 70px;

  [dir="rtl"] & {
    right: auto;
    left: 0;
  }
}

.header_ui_users {
  margin-right: 24px;
  top: 36px;

  [dir="rtl"] & {
    margin-right: 0;
    margin-left: 24px;
    right: auto;
    left: 0;
  }
}

.navbar {
  align-items: center;
  padding: 0.75rem 1rem;

  transition: background 0.3s ease;
  width: 100%;
  background: var(--sidebar-bg);

  .sidebar-toggler {
    border: 0;
    outline: 0;
    background: transparent;
    display: none;
  }

  .circle {
    border-radius: 100%;
  }

  .user-bar {
    display: flex;
    margin: 0;
    padding: 0;
    list-style: none;

    .menu-item {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 32px;
      width: 32px;
      position: relative;

      &.bordered-menu {
        border: 1px solid var(--header-bordered-menu-border-color);
        transition: 0.15s ease-in-out;

        a {
          display: flex;
          justify-content: center;
          align-items: center;

          .icon {
            font-size: 1.313rem;
          }
        }
      }

      &+.menu-item {
        margin-left: 1rem;
      }

      a {
        height: inherit;
        width: inherit;
        color: var(--header-fg);
        text-decoration: none;
        transition: 0.15s ease-in-out;

        &:hover,
        &.active {
          background-color: var(--header-active-fg);
          color: var(--sidebar-bg);
        }
      }

      .icon {
        font-size: 2rem;
      }
    }
  }
}

.unread-circle {
  width: 15px;
  height: 15px;
  background-color: #ff5252;
  border-radius: 100%;
  position: absolute;
  right: 4px;
  top: 4px;
  color: white;
  font-size: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-right: 1px;
}

@media screen and (max-width: 576px) {
  .search-bar-container {
    flex-direction: row-reverse;
  }

  .navbar {
    width: 100%;
    height: 60px;

    .sidebar-toggler {
      color: var(--header-fg);
      display: block;

      .icon {
        font-size: 1.713rem;
      }
    }

    .mobile-logo {
      display: flex;
      justify-content: center;
      align-items: center;
      flex-grow: 1;

      img {
        margin-right: 31px;
        height: 100%;
      }
    }

    .user-bar {
      position: absolute;
      left: -100vw;
      justify-content: space-around;
      align-items: center;
      height: 64px;
      width: 71vw;
      padding: 0 1rem;
      background: var(--header-mobile-bg);
      transition: 0.2s ease-in;
      will-change: left;
      z-index: 10;
    }
  }
}
</style>
